import { isAutoTransport } from 'lib/autoTransport/isAutoTransport';
import { Undef } from 'types/UtilityTypes';
import { Quoting, integrationNameMap } from 'types/enums/Integrations';

import { Suggestion } from '../types';
import {
  CONFIDENCE_THRESHOLD,
  formatSourceConfidenceList,
} from './buildCalculationSteps';

export enum SpreadConfidenceLevel {
  Low = 'low',
  Medium = 'medium',
  High = 'high',
}

export type ConfidenceBand = {
  level: SpreadConfidenceLevel;
  label: string;
  description: string;
};

export type ConfidenceDisplay = {
  level: SpreadConfidenceLevel;
  label: string;
  className: string;
  tooltipIntro: string;
  tooltipNote: Undef<string>;
  tooltipBands: ConfidenceBand[];
  tooltipSpread: string;
};

export const HIGH_SPREAD_MAX_PERCENT = 10;
export const MEDIUM_SPREAD_MAX_PERCENT = 20;
export const HIGH_SOURCE_CONFIDENCE_MIN_PERCENT = 90;
export const MEDIUM_SOURCE_CONFIDENCE_MIN_PERCENT = 70;

export const CONFIDENCE_STYLES: Record<
  SpreadConfidenceLevel,
  { label: string; className: string }
> = {
  [SpreadConfidenceLevel.High]: {
    label: 'High',
    className: 'text-success-600',
  },
  [SpreadConfidenceLevel.Medium]: {
    label: 'Medium',
    className: 'text-warning-600',
  },
  [SpreadConfidenceLevel.Low]: {
    label: 'Low',
    className: 'text-error-600',
  },
};

export const SOURCE_CONFIDENCE_LABELS: Record<SpreadConfidenceLevel, string> = {
  [SpreadConfidenceLevel.High]: 'High',
  [SpreadConfidenceLevel.Medium]: 'Moderate',
  [SpreadConfidenceLevel.Low]: 'Low',
};

const CONFIDENCE_BANDS: ConfidenceBand[] = [
  {
    level: SpreadConfidenceLevel.High,
    label: CONFIDENCE_STYLES[SpreadConfidenceLevel.High].label,
    description: `spread below ${HIGH_SPREAD_MAX_PERCENT}%`,
  },
  {
    level: SpreadConfidenceLevel.Medium,
    label: CONFIDENCE_STYLES[SpreadConfidenceLevel.Medium].label,
    description: `spread from ${HIGH_SPREAD_MAX_PERCENT}% to ${MEDIUM_SPREAD_MAX_PERCENT}%`,
  },
  {
    level: SpreadConfidenceLevel.Low,
    label: CONFIDENCE_STYLES[SpreadConfidenceLevel.Low].label,
    description: `spread over ${MEDIUM_SPREAD_MAX_PERCENT}%`,
  },
];

const SOURCE_CONFIDENCE_BANDS: ConfidenceBand[] = [
  {
    level: SpreadConfidenceLevel.High,
    label: SOURCE_CONFIDENCE_LABELS[SpreadConfidenceLevel.High],
    description: `source confidence ${HIGH_SOURCE_CONFIDENCE_MIN_PERCENT}% or higher`,
  },
  {
    level: SpreadConfidenceLevel.Medium,
    label: SOURCE_CONFIDENCE_LABELS[SpreadConfidenceLevel.Medium],
    description: `source confidence from ${MEDIUM_SOURCE_CONFIDENCE_MIN_PERCENT}% to ${HIGH_SOURCE_CONFIDENCE_MIN_PERCENT - 1}%`,
  },
  {
    level: SpreadConfidenceLevel.Low,
    label: SOURCE_CONFIDENCE_LABELS[SpreadConfidenceLevel.Low],
    description: `source confidence below ${MEDIUM_SOURCE_CONFIDENCE_MIN_PERCENT}%`,
  },
];

const SUPER_DISPATCH_SOURCE_LABEL =
  integrationNameMap[Quoting.SuperDispatch].toLowerCase();

const SUPER_DISPATCH_TOOLTIP_INTRO =
  'Confidence is based on Super Dispatch Pricing Insights confidence:';

// Builds the Confidence row on the Kit card. Auto transport uses the Super
// Dispatch score so both cards agree. Other freight uses the spread between
// rate sources.
export function buildConfidenceDisplay(
  suggestion: Suggestion
): ConfidenceDisplay {
  if (isAutoTransport(suggestion.transportType)) {
    const sourceConfidence = superDispatchSourceConfidence(suggestion);

    // A single-source auto quote has a 0% spread, which would read as High.
    // Without a Super Dispatch score, show Unavailable instead.
    if (sourceConfidence != null && sourceConfidence > 0) {
      const level = sourceLevelFromPercent(sourceConfidence);
      const style = CONFIDENCE_STYLES[level];

      return {
        level,
        label: SOURCE_CONFIDENCE_LABELS[level],
        className: style.className,
        tooltipIntro: SUPER_DISPATCH_TOOLTIP_INTRO,
        tooltipNote: undefined,
        tooltipBands: SOURCE_CONFIDENCE_BANDS,
        tooltipSpread: `Current Super Dispatch confidence: ${Math.round(sourceConfidence)}%`,
      };
    }

    return {
      level: SpreadConfidenceLevel.Low,
      label: 'Unavailable',
      className: 'text-neutral-500',
      tooltipIntro: SUPER_DISPATCH_TOOLTIP_INTRO,
      tooltipNote:
        'Super Dispatch did not provide a confidence score for this quote.',
      tooltipBands: SOURCE_CONFIDENCE_BANDS,
      tooltipSpread: 'Current Super Dispatch confidence: Unavailable',
    };
  }

  // A lone source has 0% spread, which would read as High even though Kit
  // only used it because nothing cleared the confidence threshold.
  if (suggestion.isLowConfidence) {
    const style = CONFIDENCE_STYLES[SpreadConfidenceLevel.Low];

    return {
      level: SpreadConfidenceLevel.Low,
      label: style.label,
      className: style.className,
      tooltipIntro: `Kit normally only uses sources at ${CONFIDENCE_THRESHOLD}% confidence or higher.`,
      tooltipNote: `Every source on this lane is below that, so Kit used ${formatSourceConfidenceList(suggestion.sources)} anyway.`,
      tooltipBands: [],
      tooltipSpread: `Current spread: ${suggestion.spreadPercent.toFixed(1)}%`,
    };
  }

  const level = spreadLevelFromPercent(suggestion.spreadPercent);
  const style = CONFIDENCE_STYLES[level];

  return {
    level,
    label: style.label,
    className: style.className,
    tooltipIntro:
      'Confidence is based on how much rating sources disagree (spread):',
    tooltipNote:
      suggestion.sources.length <= 1
        ? 'This quote has a single market source, so there is no spread to compare.'
        : undefined,
    tooltipBands: CONFIDENCE_BANDS,
    tooltipSpread: `Current spread: ${suggestion.spreadPercent.toFixed(1)}%`,
  };
}

export function spreadLevelFromPercent(
  spreadPercent: number
): SpreadConfidenceLevel {
  if (spreadPercent < HIGH_SPREAD_MAX_PERCENT) {
    return SpreadConfidenceLevel.High;
  }

  if (spreadPercent <= MEDIUM_SPREAD_MAX_PERCENT) {
    return SpreadConfidenceLevel.Medium;
  }

  return SpreadConfidenceLevel.Low;
}

export function sourceLevelFromPercent(
  confidencePercent: number
): SpreadConfidenceLevel {
  if (confidencePercent >= HIGH_SOURCE_CONFIDENCE_MIN_PERCENT) {
    return SpreadConfidenceLevel.High;
  }

  if (confidencePercent >= MEDIUM_SOURCE_CONFIDENCE_MIN_PERCENT) {
    return SpreadConfidenceLevel.Medium;
  }

  return SpreadConfidenceLevel.Low;
}

function superDispatchSourceConfidence(suggestion: Suggestion): Undef<number> {
  return suggestion.sources.find((source) => {
    if (typeof source.confidenceLevel !== 'number') {
      return false;
    }

    const normalizedLabel = source.label.trim().toLowerCase();
    return (
      normalizedLabel.includes(SUPER_DISPATCH_SOURCE_LABEL) ||
      normalizedLabel.includes(Quoting.SuperDispatch)
    );
  })?.confidenceLevel;
}
